import { OutlinedInput } from '@mui/material';
import FormLabel from '@mui/material/FormLabel';
import React from 'react';
import classes from 'routes/Risk/risk.module.scss';

interface Props<T> {
  readonly name: keyof T;
  readonly value: number | null;
  readonly label?: string;
  readonly fullWidth?: boolean;
  readonly disabled?: boolean;

  onChange(name: keyof T, value: number | null): void;
}

export function FormattedNumericInput<T>(props: Props<T>): React.ReactElement {
  const { name, value, label, fullWidth = true, onChange, disabled = false } = props;

  const isCommrate = name === 'commrate';
  const [isEditing, setIsEditing] = React.useState(false);
  const [editValue, setEditValue] = React.useState('');

  const handleFocus = React.useCallback((): void => {
    if (!isCommrate) return;

    setIsEditing(true);
    // Set raw value for editing (without formatting)
    if (value === null) {
      setEditValue('');
    } else {
      setEditValue(String(value));
    }
  }, [value, isCommrate]);

  const handleBlur = React.useCallback((): void => {
    if (!isCommrate) return;

    setIsEditing(false);

    const cleanValue = editValue.replace(/,/g, '').trim();

    if (cleanValue === '') {
      onChange(name, null);
      return;
    }

    const numeric = Number(cleanValue);
    if (!isNaN(numeric)) {
      // Round to 5 decimal places for commrate
      const finalValue = Math.round(numeric * 100000) / 100000;
      onChange(name, finalValue);
    }
  }, [editValue, name, onChange, isCommrate]);

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      if (disabled) {
        return;
      }

      const { value } = event.target;
      const cleanValue = value.replace(/,/g, '');

      if (isCommrate) {
        // For commrate: allow typing decimals with max 5 decimal places
        // Pattern: optional minus, digits, optional decimal point with max 5 digits after
        if (/^-?\d*\.?\d{0,5}$/.test(cleanValue)) {
          setEditValue(cleanValue);
        }
      } else {
        // For other fields: use original logic
        if (cleanValue.trim() === '') {
          onChange(name, null);
        } else {
          const numeric = Number(cleanValue);
          if (!isNaN(numeric)) {
            onChange(name, numeric);
          }
        }
      }
    },
    [name, onChange, disabled, isCommrate]
  );

  const formattedValue = React.useMemo((): string => {
    if (value === null) return '';
    return value.toLocaleString('en-US', {
      minimumFractionDigits: isCommrate ? 1 : 0,
      maximumFractionDigits: isCommrate ? 5 : 0,
    });
  }, [value, isCommrate]);

  // Format editValue with thousands separator for commrate while editing
  const formattedEditValue = React.useMemo((): string => {
    if (!editValue) return '';

    const parts = editValue.split('.');
    const integerPart = parts[0];
    const decimalPart = parts[1];

    // Add thousands separator to integer part
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    // Reconstruct with decimal part if exists
    return decimalPart !== undefined ? `${formattedInteger}.${decimalPart}` : formattedInteger;
  }, [editValue]);

  const displayValue = isCommrate && isEditing ? formattedEditValue : formattedValue;

  return (
    <>
      {label && <FormLabel>{label}</FormLabel>}
      <OutlinedInput
        name={String(name)}
        value={displayValue}
        classes={{ input: classes.numericInput }}
        fullWidth={fullWidth}
        disabled={disabled}
        onChange={handleChange}
        onFocus={isCommrate ? handleFocus : undefined}
        onBlur={isCommrate ? handleBlur : undefined}
      />
    </>
  );
}
